<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { config } from '../../config/heni-websites-config';
	import { cssVariables } from '../../utils/cssVariables/cssVariables';
	import type { RadioValue } from './types';

	let {
		dataCy,
		name = undefined,
		id = undefined,
		error = undefined,
		disabled = undefined,
		size = 'sm',
		group = $bindable(undefined),
		value = $bindable(),
		onChange = () => void 0,
		...rest
	}: Props = $props();

	interface Props {
		class?: string;
		dataCy: string;
		name?: string | undefined;
		id?: string | undefined;
		error?: boolean | undefined;
		disabled?: boolean | undefined;
		size?: 'sm' | 'md';
		group?: unknown;
		value: RadioValue;
		onChange?: (value: RadioValue) => void;
	}

	let checked = $derived(group === value);

	const handleChange = () => {
		onChange?.(value);
	};
</script>

<input
	onchange={handleChange}
	bind:group
	use:cssVariables={{
		dotColor: disabled ? config.theme.colors.gray[400] : config.theme.colors.gray[700],
		outerCircleSize: size === 'md' ? '18px' : '16px',
		dotSize: size === 'md' ? '8px' : '6px'
	}}
	data-cy={`${dataCy}-radio`}
	type="radio"
	{id}
	{name}
	{value}
	{disabled}
	{...rest}
	class={twMerge(
		classNames('border', {
			'min-h-[18px] min-w-[18px]': size === 'md',
			'min-h-[16px] min-w-[16px]': size === 'sm',
			'border-gray-400': disabled,
			'border-gray-500': !disabled && !checked,
			'border-gray-700': !disabled && checked,
			'border-red-500': error && !checked
		}),
		rest.class
	)}
/>

<style>
	input[type='radio'] {
		cursor: pointer;
		appearance: none;
		background-color: #fff;
		margin: 0;
		font: inherit;
		color: currentColor;
		width: var(--outerCircleSize);
		height: var(--outerCircleSize);
		border-radius: 50%;
		display: grid;
		place-content: center;
	}

	input[type='radio']::before {
		content: '';
		width: var(--dotSize);
		height: var(--dotSize);
		border-radius: 50%;
		transform: scale(0);
		transition: 90ms transform ease-in-out;
		box-shadow: inset 1em 1em var(--dotColor);
	}

	input[type='radio']:checked::before {
		transform: scale(1);
	}
</style>
